import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { SuperAdminService } from '../services/superAdminService';

interface DeleteBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  onDeleted: () => void;
}

export const DeleteBusinessModal: React.FC<DeleteBusinessModalProps> = ({
  isOpen,
  onClose,
  businessId,
  businessName,
  onDeleted
}) => {
  const [deletionInfo, setDeletionInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const expectedConfirmText = `DELETE ${businessName}`;

  useEffect(() => {
    if (isOpen && businessId) {
      loadDeletionInfo();
    }
  }, [isOpen, businessId]);

  const loadDeletionInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const info = await SuperAdminService.getBusinessDeletionInfo(businessId);
      setDeletionInfo(info);
    } catch (error) {
      setError('Failed to load deletion information');
      console.error('Error loading deletion info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== expectedConfirmText) {
      setError(`Please type "${expectedConfirmText}" to confirm deletion`);
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const result = await SuperAdminService.deleteBusiness(businessId, true);
      
      if (result.success) {
        onDeleted();
        onClose();
        alert(result.message || 'Business deleted successfully');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to delete business');
      console.error('Error deleting business:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!deleting) {
      setConfirmText('');
      setError(null);
      setDeletionInfo(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Business</h3>
              <p className="text-sm text-gray-500">This action cannot be undone</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={deleting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading deletion information...</span>
            </div>
          ) : deletionInfo ? (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">
                  You are about to permanently delete "{businessName}"
                </h4>
                <p className="text-sm text-red-700 mb-3">
                  This will delete the business, ALL associated data, and login/auth accounts
                  (so the email can be used to sign up again):
                </p>
                <ul className="text-sm text-red-700 space-y-1">
                  {deletionInfo.warnings.map((warning: string, index: number) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {deletionInfo.otherBusinessesForOwner?.length > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <h4 className="font-medium text-amber-900 mb-1">
                    Same owner has other businesses
                  </h4>
                  <p className="text-sm text-amber-800 mb-2">
                    Their login will NOT be deleted. Only &quot;{businessName}&quot; will be removed.
                  </p>
                  <ul className="text-sm text-amber-900 space-y-1">
                    {deletionInfo.otherBusinessesForOwner.map(
                      (b: { id: string; name: string }) => (
                        <li key={b.id}>• {b.name}</li>
                      ),
                    )}
                  </ul>
                </div>
              )}

              {/* Data Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Data to be deleted:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Users: <span className="font-medium">{deletionInfo.counts.users}</span></div>
                  <div>Bookings: <span className="font-medium">{deletionInfo.counts.bookings}</span></div>
                  <div>Customers: <span className="font-medium">{deletionInfo.counts.customers}</span></div>
                  <div>Services: <span className="font-medium">{deletionInfo.counts.services}</span></div>
                  <div>Resources: <span className="font-medium">{deletionInfo.counts.resources}</span></div>
                </div>
              </div>

              {/* Confirmation Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type "{expectedConfirmText}" to confirm deletion:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  disabled={deleting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
                  placeholder={expectedConfirmText}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">Failed to load deletion information</p>
              <button
                onClick={loadDeletionInfo}
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
              >
                Retry
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={deleting}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting || !deletionInfo || confirmText !== expectedConfirmText}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {deleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Business
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}; 